"use client";

import { useEffect, useRef, useState, type FormEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import type { Route } from "next";
import { usePathname, useRouter } from "next/navigation";
import { ShimmerOverlay } from "@/components/ui/shining-text";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import type { User } from "@supabase/supabase-js";
import type { CollectionMenuItem } from "@/sanity/lib/types";

type NavRoute = Route<"/" | "/about" | "/browse" | "/blog" | "/contact" | "/events" | "/in-store">;
type MenuRoute = Route<"/browse/rings" | "/browse/earrings" | "/browse/necklaces" | "/browse/bracelets" | "/new-arrivals" | "/events" | "/in-store">;
const navLinks: { href: NavRoute; label: string; isDisabled?: boolean }[] = [
  { href: "/", label: "Home" },
  { href: "/browse", label: "Shop" },
  { href: "/events", label: "Events" },
  { href: "/about", label: "About" },
  { href: "/blog", label: "Blog" },
  { href: "/contact", label: "Contact" }
];
// Blog is desktop-only: excluded here so it never appears in the mobile subheader.
const mobileNavLinks = navLinks.filter(
  (link) => link.href !== "/" && link.href !== "/about" && link.href !== "/blog"
);
// Desktop-only "In Store" tab, placed to the right of Events in the primary nav.
// Currently shown in both the desktop subheader and the hamburger menu's Shop list.
// Set SHOW_IN_STORE_NAV to false to hide it again without removing this code.
// The /in-store page itself stays live regardless.
const IN_STORE_NAV_LINK: { href: NavRoute; label: string; isDisabled?: boolean } = { href: "/in-store", label: "In Store" };
const SHOW_IN_STORE_NAV = true;
const secondaryNavLinks = navLinks
  .filter((link) => link.href !== "/")
  .flatMap((link) => (SHOW_IN_STORE_NAV && link.href === "/events" ? [link, IN_STORE_NAV_LINK] : [link]));
const LOGO_SRC = "/vault88-g.png";

type MenuItem = {
  label: string;
  href?: MenuRoute | NavRoute | string;
  isDisabled?: boolean;
  children?: MenuItem[];
  showPlaceholderTooltip?: boolean;
};
type MenuCategory = { category: string; items: MenuItem[] };
type MenuOption = MenuItem | MenuCategory;

function buildShopMenuCategory(collectionsTitle: string, collectionsItems: CollectionMenuItem[]): MenuCategory {
  const collectionsChildren: MenuItem[] = collectionsItems
    .filter((c): c is CollectionMenuItem => c != null && typeof c === "object" && Boolean(c.name))
    .map((c) => ({
      label: c.name,
      href: `/browse/collections/${encodeURIComponent(c.name)}` as MenuRoute
    }));

  return {
    category: "Shop",
    items: [
      { label: "Everything", href: "/browse" },
      {
        label: "Gifts",
        children: [
          { label: "Under 2000", href: "/browse/under-2000" },
          { label: "Under 1000", href: "/browse/under-1000" },
          { label: "Dream Stones", href: "/browse/dream-stones" },
          { label: "Jewelry Box Staples", href: `/browse/collections/${encodeURIComponent("Jewelry Box Staples")}` },
          { label: "Unique Pieces", href: `/browse/collections/${encodeURIComponent("Unique Pieces")}` }
        ]
      },
      ...(collectionsChildren.length > 0
        ? [{
            label: collectionsTitle,
            children: collectionsChildren
          }]
        : []),
      { label: "New Arrivals", href: "/new-arrivals" },
      { label: "By Gemstone", href: "/browse/stones" },
      { label: "Rings", href: "/browse/rings" },
      { label: "Earrings", href: "/browse/earrings" },
      { label: "Bracelets", href: "/browse/bracelets" },
      { label: "Necklaces", href: "/browse/necklaces" },
      { label: "Custom", href: "/custom" },
      // In Store hidden from the menu for now (see SHOW_IN_STORE_NAV).
      ...(SHOW_IN_STORE_NAV ? [{ label: "In Store", href: "/in-store" } as MenuItem] : [])
    ]
  };
}

const CLIENT_CARE_MENU_CATEGORY: MenuCategory = {
  category: "Client Care",
  items: [
    { label: "Caring for your pieces", href: "/caring-for-your-pieces" },
    { label: "Returns", href: "/client-care/returns" },
    { label: "Shipping", href: "/client-care/shipping" }
  ]
};

const INFO_MENU_CATEGORY: MenuCategory = {
  category: "Info",
  items: [
    { label: "About", href: "/about" },
    { label: "Events", href: "/events" }
  ]
};

const BAG_STORAGE_KEY = "vault88-cart";
const FAVORITES_STORAGE_KEY = "vault88-favorites";

type HeaderProps = {
  brandName?: string;
  logoUrl?: string;
  logoAlt?: string;
  collectionsMenuTitle?: string;
  collectionsMenuItems?: CollectionMenuItem[];
  topOffset?: number;
};

export function Header({
  brandName = "Vault 88",
  logoUrl,
  logoAlt,
  collectionsMenuTitle,
  collectionsMenuItems,
  topOffset = 0
}: HeaderProps) {
  const headerShellRef = useRef<HTMLDivElement>(null);
  const primaryBarRef = useRef<HTMLElement>(null);
  const secondaryNavRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [bagCount, setBagCount] = useState(0);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [user, setUser] = useState<User | null>(null);
  const [headerSpacerHeight, setHeaderSpacerHeight] = useState(0);
  const [secondaryNavVisible, setSecondaryNavVisible] = useState(true);
  const lastScrollYRef = useRef(0);
  const pathname = usePathname();
  const router = useRouter();
  const [searchValue, setSearchValue] = useState("");
  const resolvedLogo = logoUrl ?? LOGO_SRC;
  const resolvedAlt = logoAlt ?? `${brandName} logo`;
  const resolvedCollectionsTitle = collectionsMenuTitle || "Collections";
  const resolvedCollectionsItems = collectionsMenuItems ?? [];
  const shopMenuCategory = buildShopMenuCategory(resolvedCollectionsTitle, resolvedCollectionsItems);
  const menuOptions: MenuOption[] = [
    shopMenuCategory,
    INFO_MENU_CATEGORY,
    CLIENT_CARE_MENU_CATEGORY
  ];
  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>(() => ({
    [shopMenuCategory.category]: false,
    [INFO_MENU_CATEGORY.category]: false,
    [CLIENT_CARE_MENU_CATEGORY.category]: false
  }));
  const [expandedSubMenus, setExpandedSubMenus] = useState<Record<string, boolean>>({});

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const toggleSubMenu = (key: string) => {
    setExpandedSubMenus((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSearchSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedSearch = searchValue.trim();
    const destination = normalizedSearch ? `/browse?q=${encodeURIComponent(normalizedSearch)}` : "/browse";
    router.push(destination as Parameters<typeof router.push>[0]);
    setSearchOpen(false);
  };

  useEffect(() => {
    const updateBagCount = () => {
      if (typeof window === "undefined") return;
      try {
        const stored = window.localStorage.getItem(BAG_STORAGE_KEY);
        if (!stored) {
          setBagCount(0);
          return;
        }
        const items = JSON.parse(stored) as Array<{ id: string }>;
        setBagCount(items.length);
      } catch (error) {
        console.error("[Header] Failed to read bag", error);
        setBagCount(0);
      }
    };

    updateBagCount();

    const handleStorage = (event: StorageEvent) => {
      if (event.key === BAG_STORAGE_KEY) {
        updateBagCount();
      }
    };

    window.addEventListener("storage", handleStorage);
    const interval = window.setInterval(updateBagCount, 2000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const updateFavoritesCount = () => {
      if (typeof window === "undefined") return;
      try {
        const stored = window.localStorage.getItem(FAVORITES_STORAGE_KEY);
        setFavoritesCount(stored ? (JSON.parse(stored) as Array<{ id: string }>).length : 0);
      } catch {
        setFavoritesCount(0);
      }
    };

    updateFavoritesCount();

    const handleStorage = (event: StorageEvent) => {
      if (event.key === FAVORITES_STORAGE_KEY) updateFavoritesCount();
    };

    window.addEventListener("storage", handleStorage);
    const interval = window.setInterval(updateFavoritesCount, 2000);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    lastScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollYRef.current;

      if (currentY <= 8) {
        setSecondaryNavVisible(true);
      } else if (delta > 4) {
        setSecondaryNavVisible(false);
        setSearchOpen(false);
      } else if (delta < -4) {
        setSecondaryNavVisible(true);
      }

      lastScrollYRef.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setExpandedCategories((prev) => ({
      ...prev,
      Shop: prev.Shop ?? false,
      [CLIENT_CARE_MENU_CATEGORY.category]: prev[CLIENT_CARE_MENU_CATEGORY.category] ?? false,
      [INFO_MENU_CATEGORY.category]: prev[INFO_MENU_CATEGORY.category] ?? false
    }));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const syncSearchFromUrl = () => {
      const currentSearchQuery = (new URLSearchParams(window.location.search).get("q") ?? "").trim();
      setSearchValue(currentSearchQuery);
    };

    syncSearchFromUrl();
    window.addEventListener("popstate", syncSearchFromUrl);

    return () => {
      window.removeEventListener("popstate", syncSearchFromUrl);
    };
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const primaryBar = primaryBarRef.current;
    const secondaryNav = secondaryNavRef.current;
    if (!primaryBar || !secondaryNav) return;

    // Reserve the EXPANDED header height. We measure the primary bar's live
    // height plus the secondary nav's full content height via `scrollHeight`,
    // which ignores the collapsed `max-h-0` clamp. This keeps the reserved
    // layout space constant regardless of `secondaryNavVisible`, so toggling
    // the sub-header on scroll no longer reflows document content / shifts the
    // scroll position.
    const updateSpacerHeight = () => {
      setHeaderSpacerHeight(primaryBar.offsetHeight + secondaryNav.scrollHeight + topOffset);
    };

    updateSpacerHeight();
    // Observe both the primary bar and the secondary nav so that any real
    // layout change (including font-load reflow) triggers a re-measure.
    // The animated property on the secondary nav is `max-height`; scrollHeight
    // is invariant to max-height clamping, so re-measures during
    // collapse/expand produce the same value — no feedback loop.
    const observer = new ResizeObserver(updateSpacerHeight);
    observer.observe(primaryBar);
    if (secondaryNavRef.current) observer.observe(secondaryNavRef.current);
    window.addEventListener("resize", updateSpacerHeight);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateSpacerHeight);
    };
  }, [topOffset]);

  useEffect(() => {
    const supabase = createClient();
    supabase.auth.getUser().then(({ data }) => setUser(data.user));
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => setUser(session?.user ?? null)
    );
    return () => subscription.unsubscribe();
  }, []);

  const userInitial = user?.user_metadata?.full_name
    ? (user.user_metadata.full_name as string).charAt(0).toUpperCase()
    : user?.email?.charAt(0).toUpperCase() ?? null;

  const renderMenuItem = (item: MenuItem, keyPath: string, nested = false) => {
    const hasChildren = Boolean(item.children?.length);
    const isLink = Boolean(item.href && !item.isDisabled);
    const showTooltip =
      !hasChildren && (!item.href || item.isDisabled) && item.showPlaceholderTooltip !== false;

    if (hasChildren) {
      const isSubExpanded = expandedSubMenus[keyPath] ?? false;
      return (
        <div key={keyPath}>
          <button
            onClick={() => toggleSubMenu(keyPath)}
            className={cn(
              "flex w-full items-center justify-between rounded-lg px-3 py-2 text-left uppercase transition hover:bg-slate-100 text-slate-700",
              nested && "py-1.5 text-[12px] tracking-[0.2em]"
            )}
          >
            <span>{item.label}</span>
            <span className={cn("text-[10px] transition-transform duration-200", isSubExpanded ? "rotate-0" : "-rotate-90")}>
              ▼
            </span>
          </button>
          {isSubExpanded && (
            <div className="mt-1 flex flex-col gap-1.5 pl-4">
              {item.children!.map((child, index) => renderMenuItem(child, `${keyPath}-${index}`, true))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div key={keyPath} className={cn("relative", showTooltip && "group")}>
        {isLink ? (
          <Link
            href={item.href! as Parameters<typeof Link>[0]["href"]}
            className={cn(
              "block rounded-lg px-3 py-2 text-left uppercase transition hover:bg-slate-100 text-slate-700",
              nested && "py-1.5 text-[12px] tracking-[0.2em]"
            )}
            onClick={() => setMenuOpen(false)}
          >
            {item.label}
          </Link>
        ) : (
          <span
            className={cn(
              "block rounded-lg px-3 py-2 text-left uppercase text-slate-400 cursor-not-allowed",
              nested && "py-1.5 text-[12px] tracking-[0.2em]"
            )}
          >
            {item.label}
          </span>
        )}

        {showTooltip && (
          <span className="pointer-events-none absolute left-3 top-full z-10 mt-1 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white opacity-0 transition group-hover:opacity-100">
            {item.isDisabled ? "Coming Soon" : "Under Construction"}
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <div aria-hidden className="w-full" style={{ height: headerSpacerHeight }} />
      <div ref={headerShellRef} className="fixed inset-x-0 z-20" style={{ top: topOffset }}>
        <div className="relative">
          <header ref={primaryBarRef} className="relative z-20 bg-white/85 backdrop-blur border-b border-slate-200 px-4 py-3 shadow-sm text-slate-900">
            <div className="mx-auto grid max-w-5xl grid-cols-3 items-center gap-3">
              <div className="flex items-center">
                <button
                  type="button"
                  aria-label="Open menu"
                  className="h-10 w-10 rounded-sm bg-transparent p-2 text-slate-700 transition hover:bg-slate-100"
                  onClick={() => setMenuOpen(true)}
                >
                  <Image src="/hamburger_menu.svg" alt="" width={24} height={24} />
                </button>
              </div>
              <div className="flex items-center justify-center">
                <Link href="/" className="flex items-center justify-center">
                  <div className="group relative inline-flex items-center justify-center">
                    <Image
                      src={resolvedLogo}
                      alt={resolvedAlt}
                      width={200}
                      height={200}
                      priority
                      className="object-contain"
                    />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 mix-blend-screen transition-opacity duration-200 group-hover:opacity-80">
                      <ShimmerOverlay
                        className="h-[200px] w-[200px]"
                        style={{
                          WebkitMaskImage: `url(${resolvedLogo})`,
                          maskImage: `url(${resolvedLogo})`,
                          WebkitMaskSize: "contain",
                          maskSize: "contain",
                          WebkitMaskRepeat: "no-repeat",
                          maskRepeat: "no-repeat",
                          WebkitMaskPosition: "center",
                          maskPosition: "center"
                        }}
                      />
                    </div>
                  </div>
                </Link>
              </div>
              <div className="flex items-center justify-end" />
            </div>
          </header>
          <div
            ref={secondaryNavRef}
            className={cn(
              "overflow-hidden border-t border-slate-300 bg-white/90 backdrop-blur transition-[max-height,opacity,transform] duration-300",
              secondaryNavVisible ? "max-h-12 opacity-100 translate-y-0" : "max-h-0 opacity-0 -translate-y-2 pointer-events-none"
            )}
          >
            <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-4 px-4 py-2">
              <nav
                className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-slate-600 sm:hidden"
                aria-label="Mobile navigation"
              >
                {mobileNavLinks.map((link, index) => (
                  <div key={link.href} className={cn("relative", link.isDisabled && "group")}>
                    {link.isDisabled ? (
                      <span className={cn(
                        "inline-flex items-center gap-1 pb-0.5 text-slate-400 cursor-not-allowed",
                        index < mobileNavLinks.length - 1 &&
                          "relative after:absolute after:right-[-0.375rem] after:top-1/2 after:h-3 after:w-px after:-translate-y-1/2 after:bg-slate-300/80 after:content-['']"
                      )}>
                        {link.label}
                      </span>
                    ) : (
                      <Link
                        href={link.href}
                        className={cn(
                          "inline-flex items-center gap-1 pb-0.5 transition hover:underline hover:underline-offset-4 hover:text-slate-900",
                          index < mobileNavLinks.length - 1 &&
                            "relative after:absolute after:right-[-0.375rem] after:top-1/2 after:h-3 after:w-px after:-translate-y-1/2 after:bg-slate-300/80 after:content-['']",
                          link.href === "/"
                            ? pathname === "/" && "font-semibold text-slate-900"
                            : pathname.startsWith(link.href) && "font-semibold text-slate-900"
                        )}
                      >
                        {link.label}
                      </Link>
                    )}
                    {link.isDisabled && (
                      <span className="pointer-events-none absolute -left-2 bottom-full z-50 mb-2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white opacity-0 transition group-hover:opacity-100">
                        Coming Soon
                      </span>
                    )}
                  </div>
                ))}
              </nav>
              <nav
                className="hidden sm:flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-slate-600"
                aria-label="Primary navigation"
              >
                {secondaryNavLinks.map((link, index) => (
                  <div key={link.href} className={cn("relative", link.isDisabled && "group")}>
                    {link.isDisabled ? (
                      <span className="inline-flex items-center gap-1 pb-0.5 text-slate-400 cursor-not-allowed relative after:absolute after:right-[-0.5rem] after:top-1/2 after:h-3 after:w-px after:-translate-y-1/2 after:bg-slate-300/80 after:content-['']">
                        {link.label}
                      </span>
                    ) : (
                      <Link
                        href={link.href}
                        className={cn(
                          "inline-flex items-center gap-1 pb-0.5 transition hover:underline hover:underline-offset-4 hover:text-slate-900",
                          index < secondaryNavLinks.length - 1 &&
                            "relative after:absolute after:right-[-0.5rem] after:top-1/2 after:h-3 after:w-px after:-translate-y-1/2 after:bg-slate-300/80 after:content-['']",
                          link.href === "/"
                            ? pathname === "/" && "font-semibold text-slate-900"
                            : pathname.startsWith(link.href) && "font-semibold text-slate-900"
                        )}
                      >
                        {link.label}
                      </Link>
                    )}
                    {link.isDisabled && (
                      <span className="pointer-events-none absolute -left-2 bottom-full z-50 mb-2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white opacity-0 transition group-hover:opacity-100">
                        Coming Soon
                      </span>
                    )}
                  </div>
                ))}
              </nav>
              <div className="flex items-center gap-2">
                <Link
                  href={(user ? "/account" : "/auth") as Parameters<typeof Link>[0]["href"]}
                  className="relative inline-flex items-center justify-center p-2 text-slate-600 transition hover:text-slate-900"
                  aria-label={user ? "Account" : "Sign in"}
                >
                  {user ? (
                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[var(--primary)] text-[10px] font-bold leading-none text-white">
                      {userInitial}
                    </span>
                  ) : (
                    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                  )}
                </Link>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-sm p-2 text-slate-600 transition hover:text-slate-900"
                  aria-label={searchOpen ? "Close search" : "Open search"}
                  aria-expanded={searchOpen}
                  onClick={() => setSearchOpen((prev) => !prev)}
                >
                  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.5" y2="16.5" />
                  </svg>
                </button>
                <Link href="/favorites" className="relative inline-flex items-center justify-center p-2 text-slate-600 transition hover:text-slate-900" aria-label="Favorites">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                  {favoritesCount > 0 && (
                    <span className="absolute -top-1 -right-2 inline-flex h-4 min-w-4 items-center justify-center rounded-sm bg-royal px-1 text-center text-[10px] font-semibold leading-none text-white">
                      {favoritesCount}
                    </span>
                  )}
                </Link>
                <Link href="/cart" className="relative inline-flex items-center justify-center p-2 text-slate-600 transition hover:text-slate-900" aria-label="Bag">
                  <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 8h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 8z" />
                    <path d="M9 8V6a3 3 0 0 1 6 0v2" />
                  </svg>
                  {bagCount > 0 && (
                    <span className="absolute -top-1 -right-2 inline-flex h-4 min-w-4 items-center justify-center rounded-sm bg-royal px-1 text-center text-[10px] font-semibold leading-none text-white">
                      {bagCount}
                    </span>
                  )}
                </Link>
              </div>
            </div>
          </div>
          <div
            className={cn(
              "absolute left-0 right-0 top-full border-t border-slate-200/70 bg-white/90 backdrop-blur transition-[transform,opacity] duration-300 z-10",
              searchOpen ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0 pointer-events-none"
            )}
          >
            <form
              className="mx-auto flex w-full max-w-5xl items-center gap-3 px-4 pb-4 pt-3"
              onSubmit={handleSearchSubmit}
            >
              <label htmlFor="site-search" className="hidden text-xs uppercase tracking-[0.3em] text-slate-500 sm:block">
                Search
              </label>
              <input
                id="site-search"
                type="search"
                placeholder="Search pieces"
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
                className="flex-1 rounded-sm bg-white/80 px-4 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-sm px-2 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 transition hover:text-slate-900"
                aria-label="Run search"
              >
                Go
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-sm px-2 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-slate-500 transition hover:text-slate-900"
                aria-label="Close search"
                onClick={() => setSearchOpen(false)}
              >
                X
              </button>
            </form>
          </div>
        </div>
      </div>
      {menuOpen && <div className="fixed inset-0 z-30 bg-black/40" onClick={() => setMenuOpen(false)} />}
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-40 w-[82vw] max-w-[22rem] bg-white p-6 shadow-2xl flex flex-col gap-6 transition-transform duration-300 sm:w-96",
          menuOpen ? "translate-x-0" : "-translate-x-full pointer-events-none"
        )}
        aria-hidden={!menuOpen}
      >
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold uppercase tracking-[0.2em]">Menu</span>
          <button
            type="button"
            onClick={() => setMenuOpen(false)}
            className="rounded-sm bg-slate-900/5 px-3 py-1 text-sm uppercase tracking-wide text-slate-700 hover:bg-slate-900/10"
          >
            Close
          </button>
        </div>
        <nav className="flex flex-col gap-6 text-slate-700 text-sm uppercase tracking-[0.3em] overflow-y-auto flex-1 pr-2" aria-label="Site navigation">
          {menuOptions.map((option) => {
            const isCategory = "category" in option;

            if (isCategory) {
              const isExpanded = expandedCategories[option.category] ?? false;

              return (
                <div key={option.category} className="flex flex-col gap-3">
                  <button
                    onClick={() => toggleCategory(option.category)}
                    className="flex items-center uppercase justify-between text-sm font-bold text-slate-700 tracking-[0.3em] hover:text-slate-900 transition"
                  >
                    <span>{option.category}</span>
                    <span className={cn("transition-transform duration-200", isExpanded ? "rotate-0" : "-rotate-90")}>
                      ▼
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="flex flex-col gap-2 pl-3 overflow-hidden">
                      {option.items.map((item, index) => renderMenuItem(item, `${option.category}-${index}`))}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
        </nav>
        <Link
          href="/cart"
          className="mt-2 inline-flex items-center justify-center p-2 relative"
          aria-label="Bag"
          onClick={() => setMenuOpen(false)}
        >
          <Image src="/shopping-bag.svg" alt="" width={32} height={32} />
          {bagCount > 0 && (
            <span className="absolute -top-1 -right-1 inline-flex h-5 min-w-5 items-center justify-center rounded-sm bg-royal px-1 text-center text-xs font-semibold leading-none text-white">
              {bagCount}
            </span>
          )}
        </Link>
      </div>
    </>
  );
}
